import React, { useCallback } from 'react';
import { connect, useSelector } from 'react-redux';

import GtmService from 'static/script/app/ui/google-tagmanager-service';
import { useTranslator } from 'tradera-lang/translate';
import { usePageMeta, useScrollToTopOnMount } from 'tradera-hooks';
import ContentSwitch from 'tradera-components/content-switch';
import { LocalLoader } from 'tradera-components/loader';
import QuickView from 'tradera-components/quick-view/quick-view-modal';
import Recommendations, {
    recommendationsRequestBuilder
} from 'tradera-components/recommendations';
import Flower from '@tradera/blueprint/theme/icons/flower.svg?jsx';
import Warning from '@tradera/blueprint/theme/icons/warning.svg?jsx';
import { LayoutC } from 'tradera-apps/tradera/layout/layout-c';

const ITEMS_PER_PAGE = 36;

const RECOMMENDATION_MODES = {
    endingSoon: {
        gaCategory: 'Endless Recommendation Ending Soon',
        label: 'endlessRecommendation_endingSoon',
        slotId: 'Last_chance_infinite_scroll'
    },
    selected: {
        gaCategory: 'Endless Recommendation',
        label: 'endlessRecommendation_selected',
        slotId: 'Infinite_scroll_recommendation_slot'
    }
};

function RecommendationsPage({
    enableQuickView,
    history,
    isLoggedIn,
    match,
    pageType,
    showAds
}) {
    const {
        params: { recommendationModeId }
    } = match;
    usePageMeta(pageType);
    const { t } = useTranslator();
    useScrollToTopOnMount();

    const shippingCountryCode = useSelector(
        state => state.shippingRegion.shippingCountry.countryCodeIso2
    );
    const onSwitchMode = useCallback(
        mode => {
            history.replace(`/ma/endless-recommendation/${mode}`);
        },
        [history]
    );

    const mainContent = (
        <>
            <h1 className="heading-madrid text-center my-4">
                {isLoggedIn
                    ? t('endlessRecommendation_justForYou')
                    : t('endlessRecommendation_trendingOnTradera')}
            </h1>

            <div className="mr-n2 mx-lg-n3 mx-xl-n2">
                <ContentSwitch
                    className="pt-md-2"
                    onSwitch={onSwitchMode}
                    defaultSelected={recommendationModeId}
                    options={Object.fromEntries(
                        Object.entries(RECOMMENDATION_MODES).map(
                            ([key, mode]) => {
                                return [
                                    key,
                                    {
                                        node: (
                                            <Recommendations
                                                collapseIfEmpty={false}
                                                createRequestUrl={recommendationsRequestBuilder(
                                                    mode.slotId,
                                                    ITEMS_PER_PAGE,
                                                    shippingCountryCode
                                                )}
                                                enableQuickView={
                                                    enableQuickView
                                                }
                                                errorFallback={<ErrorMessage />}
                                                showAds={showAds}
                                                gtmLabel="Endless Recommendations"
                                                infiniteScrollEnabled={true}
                                                itemsPerPage={ITEMS_PER_PAGE}
                                                loader={
                                                    <div
                                                        className="position-relative"
                                                        style={{
                                                            minHeight: '50vh'
                                                        }}>
                                                        <LocalLoader />
                                                    </div>
                                                }
                                                noMoreItemsMarker={
                                                    <NoMoreItemsMessage />
                                                }
                                                onReachedEnd={trackReachedEndOfRecommendation(
                                                    mode.gaCategory
                                                )}
                                            />
                                        ),
                                        label: t(mode.label)
                                    }
                                ];
                            }
                        )
                    )}
                />
            </div>
            <QuickView />
        </>
    );

    return <LayoutC mainContent={mainContent} />;
}

const NoMoreItemsMessage = () => {
    const { t } = useTranslator();
    return (
        <div
            className="col-12 text-center py-5"
            data-recommendations-no-more-items>
            <Flower className="icon icon-xl icon-dark mb-2" />
            <p className="size-paris mb-3">
                {t('endlessRecommendation_noMoreItemsHeading')}
            </p>
            <p className="my-2">{t('endlessRecommendation_noMoreItemsText')}</p>
        </div>
    );
};

const ErrorMessage = () => {
    const { t } = useTranslator();
    return (
        <div className="col-12 text-center py-5" data-recommendations-error>
            <Warning className="icon icon-lg icon-dark mb-1" />
            <p className="size-paris mb-3">
                {t('endlessRecommendation_errorHeading')}
            </p>
            <p className="my-2">{t('endlessRecommendation_errorText')}</p>
            <p className="my-2">
                {t('endlessRecommendation_errorTryAgainLater')}
            </p>
            <div className="col-12 text-center my-4">
                <a
                    data-recommendations-reload
                    role="button"
                    className="btn btn-primary text-white size-paris"
                    href={window.location.href}>
                    {t('endlessRecommendation_errorRecommendedAction')}
                </a>
            </div>
        </div>
    );
};

const mapStateToProps = state => {
    return {
        enableQuickView: state.environment.featureSwitches['quick-view'],
        showAds:
            state.environment.featureSwitches[
                'show-ads-for-recommendation-pages'
            ],
        isLoggedIn: state.member.isLoggedIn
    };
};

const trackReachedEndOfRecommendation = category => itemIds => {
    GtmService.trackAction(
        category,
        'Scroll reached end',
        'Number of results',
        itemIds.length
    );
};

export default connect(mapStateToProps)(RecommendationsPage);
